<template>
  <div
    data-test="graphics-videoSettings"
  >
    <SidebarSettingsGroup
      title="Video Settings"
      tooltip
      collapsible
      :element-type="element.type"
      tooltip-label="All video settings can be adjusted in Producer."
      tooltip-icon="fas fa-info-circle"
      tooltip-style="info"
      tooltip-pos="down"
      data-test="graphics-videoSettings-videoSettings"
    >
      <div class="sidebar-form-group">
        <label class="checkbox">Autoplay
          <input
            v-model="autoplay"
            type="checkbox"
            data-test="graphics-videoSettings-toggleAutoplay"
            data-entity="graphics-videoSettings-toggleAutoplay"
          >
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>
        <label class="checkbox">Loop
          <input
            v-model="loop"
            type="checkbox"
            data-test="graphics-videoSettings-toggleLoop"
            data-entity="graphics-videoSettings-toggleLoop"
          >
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>
        <label class="checkbox">Start Muted
          <input
            v-model="muted"
            type="checkbox"
            data-test="graphics-videoSettings-toggleMuted"
            data-entity="graphics-videoSettings-toggleMuted"
          >
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>
      </div>

      <template v-if="!editingMultiple">
        <hr class="sidebar-divider">
        <asset-changer
          :preview-image="element.posterUrl"
          asset-type="video"
          @modal="$emit('modal', $event)"
          @select-asset="changeVideo"
        />

        <hr class="sidebar-divider">

        <div
          class="sidebar-form-group in-out-slider"
          role="group"
          aria-labelledby="in-out-label"
        >
          <div class="settings-col-12 settings-col-xs">
            <label
              id="in-out-label"
              class="sidebar-form-label"
            >In/Out</label>
          </div>

          <div class="settings-col-12 settings-col-xs">
            <div class="range-slider">
              <input
                v-model.number="startTimeInSeconds"
                class="range-slider__input"
                type="range"
                min="0"
                step="0.1"
                :max="Math.ceil(element.duration)"
                data-test="graphics-videoSettings-setStartTime"
                data-entity="graphics-videoSettings-setStartTime"
              >
              <input
                v-model.number="endTimeInSeconds"
                class="range-slider__input"
                type="range"
                min="0"
                step="0.1"
                :max="Math.ceil(element.duration)"
                data-test="graphics-videoSettings-setEndTime"
                data-entity="graphics-videoSettings-setEndTime"
              >
            </div>
          </div>
        </div>

        <div class="sidebar-form-group">
          <div class="settings-col-6 settings-col-xs">
            <div class="input-group">
              <input
                v-model.lazy="startTimeFormatted"
                type="text"
                name="inTime"
                :class="{ 'is-invalid': validationErrors.inTime }"
                data-test="graphics-videoSettings-setInTime"
                data-entity="graphics-videoSettings-setInTime"
              >
            </div>
            <label class="sidebar-input-sub-label">In</label>
          </div>
          <div class="settings-col-6 settings-col-xs">
            <div class="input-group">
              <input
                v-model.lazy="endTimeFormatted"
                type="text"
                name="outTime"
                :class="{ 'is-invalid': validationErrors.outTime }"
                data-test="graphics-videoSettings-setOutTime"
                data-entity="graphics-videoSettings-setOutTime"
              >
            </div>
            <label class="sidebar-input-sub-label">Out</label>
          </div>
        </div>
        <div class="video-actions">
          <button
            class="btn video-actions__button"
            :class="{ 'video-actions__button--active': playing }"
            data-test="graphics-videoSettings-playPause"
            data-entity="graphics-videoSettings-playPause"
            @click="play"
          >
            <template v-if="playing">
              <span class="fas fa-pause fa-sm" />
              Pause
            </template>
            <template v-else>
              <span class="fas fa-play fa-sm" />
              Play
            </template>
          </button>
          <button
            class="btn video-actions__button"
            :class="{ 'video-actions__button--active': currentlyMuted }"
            data-test="graphics-videoSettings-muteUnmute"
            data-entity="graphics-videoSettings-muteUnmute"
            @click="mute"
          >
            <template v-if="currentlyMuted">
              <span class="fas fa-volume-mute fa-sm" />
              Unmute
            </template>
            <template v-else>
              <span class="fas fa-volume fa-sm" />
              Mute
            </template>
          </button>
        </div>
      </template>
    </SidebarSettingsGroup>

    <SidebarSettingsGroup
      title="Style & Animation"
      collapsible
      :element-type="element.type"
    >
      <opacity-settings v-model="opacity" />

      <hr class="sidebar-divider">

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Border
          <input
            v-model="border"
            type="checkbox"
            data-test="graphics-videoSettings-toggleBorder"
          >
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <div
          v-if="border"
          class="settings-row settings-row-xs"
        >
          <div class="settings-col-6 settings-col-xs">
            <validation-field as="input"
              v-model.number="borderWidth"
              rules="required|numeric|min:0"
              type="number"
              name="borderWidth"
              min="0"
              :class="{ 'is-invalid': validationErrors['borderWidth'] }"
              data-test="graphics-videoSettings-setBorderWidth"
              data-entity="graphics-videoSettings-setBorderWidth"
            />
            <label class="sidebar-input-sub-label">Width</label>
          </div>
          <div class="settings-col-6 settings-col-xs colorpicker">
            <colorpicker v-model="borderColor" />
            <label class="sidebar-input-sub-label">Color</label>
          </div>
        </div>
      </div>

      <hr class="sidebar-divider">

      <box-shadow-settings :element="element" />

      <hr class="sidebar-divider">

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Blur
          <input
            v-model="blur"
            type="checkbox"
            data-test="graphics-videoSettings-toggleBlur"
            data-entity="graphics-videoSettings-toggleBlur"
          >
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <div
          v-if="blur"
          class="input-group"
        >
          <validation-field as="input"
            v-model.number="blurSize"
            rules="required|numeric|min:0"
            type="number"
            name="blurSize"
            min="0"
            :class="{ 'is-invalid': validationErrors['blurSize'] }"
            data-test="graphics-videoSettings-setBlurSize"
            data-entity="graphics-videoSettings-setBlurSize"
          />
          <span class="input-group-addon">px</span>
        </div>
      </div>

      <hr class="sidebar-divider">

      <animation-settings :element="element" />
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import EventBus from '../../utils/event-bus';
import SettingsMixins from '../../mixins/settings-mixins';

import AnimationSettings from './Animation.vue';
import OpacitySettings from './Opacity.vue';
import BoxShadowSettings from './BoxShadow.vue';
import AssetChanger from './AssetChanger.vue';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

export default {
  emits: ['modal'],
  name: 'VideoSettings',
  components: {
    'animation-settings': AnimationSettings,
    'opacity-settings': OpacitySettings,
    'box-shadow-settings': BoxShadowSettings,
    AssetChanger,
  },
  mixins: [SettingsMixins],
  props: [
    'element',
    'tooltip',
    'tooltipLabel',
    'tooltipIcon',
    'tooltipStyle',
    'tooltipPos',
  ],
  data() {
    return {
      currentlyMuted: true,
    };
  },
  computed: {
    autoplay: {
      get() {
        return this.getPropertyValue('videoOptions.autoplay');
      },
      set(v) {
        this.updateOneElementValue('videoOptions.autoplay', v);
      },
    },
    loop: {
      get() {
        return this.getPropertyValue('videoOptions.loop');
      },
      set(v) {
        this.updateOneElementValue('videoOptions.loop', v);
      },
    },
    muted: {
      get() {
        return this.getPropertyValue('videoOptions.muted');
      },
      set(v) {
        this.updateOneElementValue('videoOptions.muted', v);
      },
    },
    opacity: {
      get() {
        return this.getPropertyValue('styles.opacity', { emptyValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },
    border: {
      get() {
        return this.getPropertyValue('border');
      },
      set(v) {
        this.updateOneElementValue('border', v);
      },
    },
    borderWidth: {
      get() {
        return this.getPropertyValue('borderWidth');
      },
      set(v) {
        this.updateOneElementValue('borderWidth', v);
      },
    },
    borderColor: {
      get() {
        return this.getPropertyValue('borderColor');
      },
      set(v) {
        this.updateOneElementValue('borderColor', v);
      },
    },
    blur: {
      get() {
        return this.getPropertyValue('blur');
      },
      set(v) {
        this.updateOneElementValue('blur', v);
      },
    },
    blurSize: {
      get() {
        return this.getPropertyValue('blurSize');
      },
      set(v) {
        this.updateOneElementValue('blurSize', v);
      },
    },

    playing() {
      return this.element.videoOptions.playing;
    },
    startTimeInSeconds: {
      get() {
        return this.element.videoOptions.start;
      },
      set(v) {
        let newStart = v;

        if (v < 0) {
          newStart = 0;
        } else if (v >= this.element.duration) {
          newStart = this.element.duration;
          this.updateOneElementValue('videoOptions.end', false);
        }

        if (v > this.endTimeInSeconds) {
          this.endTimeInSeconds = v;
        }

        this.updateOneElementValue('videoOptions.start', newStart);
        this.updateVideoTrimOptions(newStart);
      },
    },
    startTimeFormatted: {
      get() {
        return dayjs.duration(this.startTimeInSeconds * 1000).format('m:ss');
      },
      set(v) {
        this.startTimeInSeconds = this.convertToSeconds(v);
      },
    },
    endTimeInSeconds: {
      get() {
        if (this.element.videoOptions.end === false) { return Math.ceil(this.element.duration); }
        return this.element.videoOptions.end;
      },
      set(v) {
        if (v >= this.element.duration) {
          this.updateOneElementValue('videoOptions.end', false);
        } else {
          this.updateOneElementValue('videoOptions.end', v);
          this.updateVideoTrimOptions(v);
        }

        if (v < this.startTimeInSeconds) {
          this.startTimeInSeconds = v;
        }
      },
    },
    endTimeFormatted: {
      get() {
        return dayjs.duration(this.endTimeInSeconds * 1000).format('m:ss');
      },
      set(v) {
        this.endTimeInSeconds = this.convertToSeconds(v);
      },
    },
  },
  methods: {
    ...mapActions('graphicsTemplate', [
      'setElementVideoOptionsValueByProp',
    ]),

    convertToSeconds(input) {
      const digitStr = input.replace(/\D/g, ''); // remove non digit chars
      const digitArr = digitStr.split('');

      return digitArr.reverse().reduce((total, d, idx) => {
        const numD = Number(d);

        switch (idx) {
          case 1: // 10s seconds
            return total + numD * 10;
          case 2: // minutes
            return total + numD * 60;
          case 3: // 10s minutes
            return total + numD * 600;

            // TODO: support for longer than 1 hour?

          default:
            return total + numD;
        }
      }, 0);
    },
    updateVideoTrimOptions(time) {
      EventBus.emit('update-video-trim-options', time);
    },
    play() {
      EventBus.emit('play-pause-video');
    },
    mute() {
      this.currentlyMuted = !this.currentlyMuted;
      EventBus.emit('mute-video');
    },
    changeVideo({ asset }) {
      this.updateOneElementValue('src', asset?.media_url || '');
      this.updateOneElementValue('posterUrl', asset?.thumbnail_url || '');
      this.updateOneElementValue('duration', asset?.meta?.duration || 0);
      this.updateOneElementValue('contentType', asset?.meta?.content_type || '');
    },
  },
};
</script>
